<template>
  <ValidationObserver
    ref="dataObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="edit-billing-information-modal"
      ref="editBillingInformationModal"
      modal-class="settings-modal no-multiselect-overflow"
      :body-class="['body-wrapper', 'white-bg']"
      title="Edit billing information"
      hide-header-close
      size="md"
      centered
      
      @hidden="clear">
      <!-- Company name -->
      <b-form-group class="no-margin-bottom">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          class="greedy"
          name="Company name" >
          <label class="settings__label">Company name</label>
          <input
            v-model="companyName"
            type="text"
            class="w-100"
            placeholder="Enter company name"
            @input="processForm" >
          <div
            v-if="errors[0]"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </b-form-group>

      <!-- Country -->
      <b-form-group class="no-margin-bottom">
        <ValidationProvider
          ref="countryProviderRef"
          v-slot="{ errors }"
          mode="aggressive"
          name="Country" >
          <label class="settings__label">Country</label>
          <multiselect
            v-model="country"
            placeholder="Select an option"
            class="flex-options hide-tags no-selected-background"
            :options="countryList"
            label="name"
            track-by="code"
            :multiple="false"
            :show-labels="false"
            :show-no-results="false"
            :close-on-select="true"
            :preserve-search="false"
            :allow-empty="true"
            :searchable="true"
            @input="countryRequiredTest" >
            <template
              slot="option"
              slot-scope="props">
              <SBIcon
                :icon="SBIconList.DONE_TICK"
                :color="SBColors.bluePrimary"
                :size="9"/>
              {{ props.option.name }}
            </template>
          </multiselect>
          <div
            v-if="errors[0]"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errors[0] }}</span>
          </div>
          <div
            v-else-if="foceInvalid"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>Country is required if you populate the Tax ID</span>
          </div>
        </ValidationProvider>
      </b-form-group>

      <!-- Person Name -->
      <b-form-group class="no-margin-bottom">
        <div class="flex align-baseline">
          <label class="settings__label greedy">Person</label>
          <span class="optional">Optional</span>
        </div>
        <input
          v-model="personName"
          type="text"
          class="w-100"
          placeholder="Enter first and last name"
          @input="processForm" >
      </b-form-group>

      <!-- Email -->
      <b-form-group class="no-margin-bottom" >
        <ValidationProvider
          v-slot="{ errors }"
          rules="email"
          name="Email"
          mode="eager" >
          <div class="flex align-baseline">
            <label class="settings__label greedy">Billing email</label>
            <span class="optional">Optional</span>
          </div>
          <b-form-input
            id="email"
            v-model="billingEmail"
            name="email"
            type="email"
            placeholder="Enter billing email"
            class="sb2-bordered sb2-hoverable sb2-focusable"
            trim
            @input="processForm" />
          <div
            v-if="errors[0]"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </b-form-group>

      <!-- Address -->
      <b-form-group class="no-margin-bottom">
        <div class="flex align-baseline">
          <label class="settings__label greedy">Address</label>
          <span class="optional">Optional</span>
        </div>
        <input
          v-model="address"
          type="text"
          class="w-100"
          placeholder="Enter address"
          @input="processForm" >
      </b-form-group>

      <!-- City -->
      <b-form-group class="no-margin-bottom">
        <div class="flex align-baseline">
          <label class="settings__label greedy">City</label>
          <span class="optional">Optional</span>
        </div>
        <input
          v-model="city"
          type="text"
          class="w-100"
          placeholder="Enter city"
          @input="processForm" >
      </b-form-group>

      <!-- State -->
      <b-form-group class="no-margin-bottom">
        <div class="flex align-baseline">
          <label class="settings__label greedy">State</label>
          <span class="optional">Optional</span>
        </div>
        <input
          v-if="!country || !country.code || !['US', 'CA'].includes(country.code)"
          v-model="statesText"
          type="text"
          class="w-100"
          placeholder="Enter state"
          @input="processForm" >
        <multiselect
          v-else
          v-model="statesObject"
          placeholder="Select an option"
          class="flex-options hide-tags no-selected-background"
          :options="statesList[country.code]"
          label="name"
          track-by="code"
          :multiple="false"
          :show-labels="false"
          :show-no-results="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="false"
          :allow-empty="false"
          :searchable="true"
          @input="processForm" >
          <template
            slot="option"
            slot-scope="props">
            <SBIcon
              :icon="SBIconList.DONE_TICK"
              :color="SBColors.bluePrimary"
              :size="9"/>
            {{ props.option.name }}
          </template>
        </multiselect>
      </b-form-group>

      <!-- Postal code -->
      <b-form-group class="no-margin-bottom">
        <div class="flex align-baseline">
          <label class="settings__label greedy">Postal code</label>
          <span class="optional">Optional</span>
        </div>
        <input
          v-model="postalCode"
          type="text"
          class="w-100"
          placeholder="Enter postal code"
          @input="processForm" >
      </b-form-group>

      <!-- Postal code -->
      <b-form-group class="no-margin-bottom">
        <ValidationProvider
          v-slot="{ errors }"
          :rules="`taxId`"
          name="Tax ID"
          mode="eager" >
          <div class="flex align-baseline">
            <label class="settings__label greedy">Tax ID</label>
            <span class="optional">Optional</span>
          </div>
          <input
            v-model="taxID"
            type="text"
            class="w-100"
            placeholder="Enter tax ID"
            @input="countryRequiredTest" >
          <div
            v-if="errors[0]"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errors[0] }}</span>
          </div>
          <div
            v-else-if="taxIdError"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ taxIdError }}</span>
          </div>
        </ValidationProvider>
      </b-form-group>

      <template slot="modal-footer">
        <div class="modal-footer__buttons flex flex-row flex-nowrap">
          <b-button @click="hide">Cancel</b-button>
          <b-button
            variant="primary"
            :disabled="foceInvalid || saving || !valid || !formChanged"
            :class="{loading: saving}"
            class="flex settings__actions--save"
            @click="confirm" >
            <span>Save</span>
            <Spinner
              v-if="saving"
              :size="14"
              :thickness="2"
              :color="SBColors.white" />
          </b-button>
        </div>

      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { extend } from 'vee-validate';

import api from '@/helpers/api';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import countryList from '@/helpers/lists/countries';
import statesList from '@/helpers/lists/states';
import Spinner from '@/components/common/Spinner';


export default {
  name: 'EditBillingInformationModal',
  components: {
    SBIcon,
    Spinner,
    Multiselect,
  },
  data() {
    return {
      companyName: '',
      country: null,
      personName: '',
      billingEmail: '',
      address: '',
      city: '',
      statesText: '',
      statesObject: null,
      postalCode: '',
      taxID: '',
      saving: false,
      initialFormData: {},
      latestFormData: {},
      foceInvalid: false,
      taxIdError: null,
      SBColors,
      SBIconList,
      countryList,
      statesList,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    formChanged() {
      return !this.$lodash.isEqual({ ...this.initialFormData }, { ...this.latestFormData });
    },
  },
  watch: {
    taxID() {
      this.taxIdError = null;
    },
  },
  methods: {
    clear() {
      this.saving = false;
    },
    confirm() {
      this.saving = true;
      const data = {
        billing_name: this.companyName,
        billing_email: this.billingEmail,
        // billing_description: '',
        billing_address: this.address,
        billing_country_code: this.country ? this.country.code : '',
        billing_state: this.country && ['US', 'CA'].includes(this.country.code) && this.statesObject ? this.statesObject.name : this.statesText,
        billing_city: this.city,
        billing_zip_code: this.postalCode,
        // shipping_address: '',
        // shipping_country_code: '',
        // shipping_city: '',
        // shipping_zip_code: '',
        person_name: this.personName,
        tax_id: this.taxID,
        includeMe: true, // this is added yust so the socket_id is not added to the request
      };

      // Filter out all unset values because backend will otherwise try to validate minimum length values and fail.
      Object.keys(data).forEach((key) => {
        if (data[key] === '') {
          delete data[key];
        }
      });
      api('PUT', `organisation/${this.currentOrganisation.id}/billing/details`, data, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.$emit('reload-billing-details');
            this.hide();
          }
        })
        .catch((error) => {
          if (error.response.data.message === 'Invalid Tax ID') {
            this.taxIdError = error.response.data.message;
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    hide() {
      this.$refs.editBillingInformationModal.hide();
    },
    show(details = {}) {
      this.populateForm(details);
      this.$refs.editBillingInformationModal.show();
    },
    populateForm(details = {}) {
      const detailsAsObject = Object.fromEntries(details.map(item => [item.title, item.value]));

      // Populate form with current data
      this.companyName = detailsAsObject['Company name'];
      this.country = this.countryList.find(country => country.code === detailsAsObject.Country);
      this.personName = detailsAsObject.Person;
      this.billingEmail = detailsAsObject['Billing email'];
      this.address = detailsAsObject.Address;
      this.city = detailsAsObject.City;
      this.statesText = ['US', 'CA'].includes(detailsAsObject.Country) ? '' : detailsAsObject.State;
      this.statesObject = ['US', 'CA'].includes(detailsAsObject.Country) && detailsAsObject.State ? this.statesList[detailsAsObject.Country].find(state => state.name === detailsAsObject.State) : null;
      this.postalCode = detailsAsObject['Postal code'];
      this.taxID = detailsAsObject['Tax ID'];

      this.processForm();
      this.initialFormData = { ...this.latestFormData };
      this.$refs.dataObserver.reset();
    },
    processForm() {
      this.latestFormData = {
        name: this.companyName,
        country: this.country,
        personName: this.personName,
        email: this.billingEmail,
        address: this.address,
        city: this.city,
        statesText: this.statesText,
        statesObject: this.statesObject,
        postalCode: this.postalCode,
        taxId: this.taxID,
      };
    },
    // Since there is an issue with vee-validate to detect clear on country value then we need to do validation manually. Also the manual vee validate error adding is not working.
    countryRequiredTest() {
      this.processForm();
      this.foceInvalid = !!this.taxID && !this.country;
    },
  },
};
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 0 !important;

  &:first-of-type label {
    margin-top: 0 !important;
  }

  .optional {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: $greyPrimary !important;
  }
}
</style>
